// Which dress styles a person has switched on ("choose dress" in Settings, or the gear beside the dress grid in the editor).
//
// The saved list (user.settings.enabledDressStyles) names only the styles that existed when it was saved — empty / null means
// "everything". So a style added LATER would stay hidden for everyone who ever saved a list. Styles added after that point are
// listed here: they are ON unless the person turned them off, and that choice is kept in the same list as "!<style>".
// When another style is added in future, append it here (and leave the earlier ones in place — their "!…" marks stay valid).
export const NEW_DEFAULT_ON_DRESS_STYLES = ['abaya', 'thobe', 'thobe-ghutra', 'nehru-jacket', 'hijab-formal'];

// Which styles are on BY DEFAULT depends on the person's country (nothing saved yet): Bangladesh (or no country set) starts with the
// Bangladeshi-style wardrobe, everyone else starts with the Gulf / international one. Anyone can still switch any style on or off.
const BD_ONLY_DRESS_STYLES = ['sharee', 'sharee-design', 'panjbi', 'kameez-hijab', 'shari-hijab', 'orna-kameez'];
const GULF_DRESS_STYLES = ['abaya', 'thobe', 'thobe-ghutra', 'nehru-jacket', 'hijab-formal'];
export const isBdCountry = (country) => !country || String(country).toUpperCase() === 'BD';
export const isDressStyleDefaultOn = (value, country) => (isBdCountry(country) ? !GULF_DRESS_STYLES.includes(value) : !BD_ONLY_DRESS_STYLES.includes(value));

/** Is this style shown, given the saved list (and the person's country for the untouched default)? */
export const isDressStyleEnabled = (list, value, country) => {
    if (!list?.length) return isDressStyleDefaultOn(value, country);
    if (list.includes(value)) return true;
    if (list.includes(`!${value}`)) return false;
    return NEW_DEFAULT_ON_DRESS_STYLES.includes(value) && isDressStyleDefaultOn(value, country);
};

/**
 * The saved list after switching ONE style on or off — null when the result is exactly the country default.
 * `allValues` = every style the person can see. Older styles are listed when on; a newer style is only marked when it differs from
 * its default ("!<style>" = off although on by default, "<style>" = on although off by default), so switching one off and on again
 * gives back exactly the list that was saved before (the Settings Save button relies on that to go back to disabled).
 */
export const toggleDressStyle = (list, allValues, value, country) => {
    const sel = new Map(allValues.map((v) => [v, isDressStyleEnabled(list, v, country)]));
    sel.set(value, !sel.get(value));
    if (allValues.every((v) => sel.get(v) === isDressStyleDefaultOn(v, country))) return null;
    const next = (list || []).filter((v) => !allValues.includes(v.replace(/^!/, '')));
    for (const v of allValues) {
        const on = sel.get(v);
        if (NEW_DEFAULT_ON_DRESS_STYLES.includes(v)) {
            const d = isDressStyleDefaultOn(v, country);
            if (on && !d) next.push(v);
            else if (!on && d) next.push(`!${v}`);
        } else if (on) next.push(v);
    }
    return next;
};

// Every dress style there is (the order of the pickers) — only used to answer "is this the same selection?" below, so a Settings
// Save button can go back to disabled when someone switches a style off and on again. Add a new style here too.
export const DRESS_STYLE_VALUES = [
    'shirt', 't-shirt', 'suite', 'suite-tieless', 'panjbi', 'tops', 'round-collar-tshirt', 'sharee', 'sharee-design',
    'hizab', 'kameez-hijab', 'shari-hijab', 'orna-kameez', 'jacket', ...NEW_DEFAULT_ON_DRESS_STYLES,
];

/** Do two saved lists switch exactly the same styles on? (null, [] and "every style listed" all mean everything.) */
export const sameDressStyleSelection = (a, b, country) =>
    DRESS_STYLE_VALUES.every((v) => isDressStyleEnabled(a, v, country) === isDressStyleEnabled(b, v, country));
